import { APRow, APText, APPalette, APImage, APButton, APSizedBox, APColumn, APExpanded, useDeviceType, showSnackbar } from "ap-components";
import saveAs from "file-saver";
import html2canvas from "html2canvas";
import { useRef, useState } from "react";
import { APCharts } from "../../../Common/Charting";
import CalculatorFormulas from "../CalculatorFormulas";
import CalculatorInput from "../CalculatorInput";
import { ChartValue, ScreenShot } from "./Emi";

export function getYearMonth(tenure: number) {
    var year = Math.floor(tenure / 12);
    var month = tenure % 12;
    var res;
    if (month == 0) {
        res = " " + year + " Year "
    } else {
        res = " " + year + " Years " + month + " Months "
    }

    return res;
}

export default function Swp() {

    const targetElementRef = useRef<HTMLDivElement>(null);
    const deviceType = useDeviceType();

    const in1Ref = useRef<HTMLDivElement>(null);
    const in2Ref = useRef<HTMLDivElement>(null);
    const in3Ref = useRef<HTMLDivElement>(null);
    const in4Ref = useRef<HTMLDivElement>(null);
    const in5Ref = useRef<HTMLDivElement>(null);

    async function captureScreenshot() {
        if (targetElementRef.current) {
            try {

                in1Ref.current?.style.setProperty("display", "none", "important");
                in2Ref.current?.style.setProperty("display", "block", "important");
                in3Ref.current?.style.setProperty("display", "none", "important");
                in4Ref.current?.style.setProperty("display", "block", "important");
                in5Ref.current?.style.setProperty("display", "block", "important");

                const canvas = await html2canvas(targetElementRef.current);
                const url = canvas.toDataURL();
                saveAs(url, "Swp Calculator")
            } catch (error) {
                console.error('Error capturing screenshot:', error);
                showSnackbar('Could not create the snapshot. Please try again.', 'negative');
            } finally {
                // Restore the live view in finally — a throw from html2canvas (likely on phones)
                // must not leave the calculator stuck in snapshot mode with no way back.
                in1Ref.current?.style.setProperty("display", "block", "important");
                in2Ref.current?.style.setProperty("display", "none", "important");
                in3Ref.current?.style.setProperty("display", "block", "important");
                in4Ref.current?.style.setProperty("display", "none", "important");
                in5Ref.current?.style.setProperty("display", "none", "important");
            }
        }
    };

    const [initialValue, setInitialValue] = useState({
        totalInvestment: 5000000,
        withdrawalPerMonth: 25000,
        expectedReturns: 12,
        withdrawalPeriod: 10,
        waitingPeriodBeforeWithdrawal: 1,
        withdrawalIncreaseRate: 5
    });

    var leftBalance = 0
    for (var j = 1; j <= initialValue.waitingPeriodBeforeWithdrawal; j++) {
        var initialCorpus = initialValue.totalInvestment;
        var maturityAmountPerMonth: number = (j == 1 ? initialCorpus : leftBalance) * (1 + initialValue.expectedReturns / (100));
        leftBalance = maturityAmountPerMonth;
    }
    const totalCorpus = leftBalance === 0 ? initialValue.totalInvestment : leftBalance;

    var out = CalculatorFormulas.swpCalculator(initialValue);

    var isIncreasing = out[out.length - 1]?.isContinuousIncreasing

    // deplete time -> Time in which maturityAmount becomes 0 or  min positive value a/q given input.
    var depleteTime: any;
    let corpusToggle = false;

    var positiveMaturityAmount = out.map((item, index) => {
        if (item.maturityAmountPerMonth > 0) {
            return {
                maturityAmountPerMonth: item.maturityAmountPerMonth,
                time: item.time,
                depleteTime: item.depleteTime,
                isContinuousIncreasing: item.isContinuousIncreasing
            }
        } else {
            corpusToggle = true;

        }
    }).filter(Boolean);



    //  For showing the bar of total Investment on X axis for x=0
    positiveMaturityAmount.unshift({
        maturityAmountPerMonth: totalCorpus,
        time: 0,
        depleteTime: 0,
        isContinuousIncreasing: undefined
    });



    depleteTime = positiveMaturityAmount[positiveMaturityAmount.length - 1]?.depleteTime

    if (isIncreasing) {
        var progressiveTime = positiveMaturityAmount[positiveMaturityAmount.length - 1]?.time

        if (progressiveTime)
            if (initialValue.withdrawalPeriod < 2) {
                progressiveTime = progressiveTime
            } else {
                progressiveTime = progressiveTime * 12
            }
    }

    var maturityAmount: number = 0;
    if (out) {
        maturityAmount = (positiveMaturityAmount[positiveMaturityAmount.length - 1]?.maturityAmountPerMonth ?? 0)
    }
    var defaultState: boolean = false;
    if (initialValue.expectedReturns == 0 || initialValue.withdrawalPerMonth == 0 || initialValue.totalInvestment == 0 || initialValue.withdrawalPeriod == 0) {
        defaultState = true;
    }

    var shouldWithdrawPerMonth;
    if (corpusToggle) {

        function calculateInitialWithdrawal(
            principal: number,
            annualRate: number,
            years: number,
            annualGrowth: number
        ): number {
            const r = annualRate / 12 / 100; // Monthly interest rate
            const g = annualGrowth / 100;    // Annual growth rate
            const n = years;
            const months = 12;

            const computePresentValue = (A: number): number => {
                let pv = 0;
                for (let year = 0; year < n; year++) {
                    const yearlyWithdrawal = A * Math.pow(1 + g, year);
                    for (let month = 0; month < months; month++) {
                        const monthsPassed = year * 12 + month;
                        pv += yearlyWithdrawal / Math.pow(1 + r, monthsPassed);
                    }
                }
                return pv;
            };

            // Binary search for A such that PV of withdrawals equals the principal
            let low = 0;
            let high = principal;
            const epsilon = 0.01;

            while (high - low > epsilon) {
                const mid = (low + high) / 2;
                const pv = computePresentValue(mid);

                if (pv > principal) {
                    high = mid;
                } else {
                    low = mid;
                }
            }

            return Math.round((low + high) / 2 * 100) / 100;
        }
        const totalInvestment = totalCorpus;
        const rate = initialValue.expectedReturns;
        const years = initialValue.withdrawalPeriod;
        const growth = initialValue.withdrawalIncreaseRate;

        shouldWithdrawPerMonth = calculateInitialWithdrawal(
            totalInvestment,
            rate,
            years,
            growth
        );
        shouldWithdrawPerMonth = shouldWithdrawPerMonth - 0.01

    }

    var option = {
        legend: {
            show: true,
            left: "1%"
        },

        grid: {
            left: '12%',
            bottom: "15%",
            right: "5%"
        },

        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'none',
            },
        },

        xAxis: {
            type: 'category',
            data: positiveMaturityAmount?.map((e) => e?.time),
            axisPointer: {
                type: 'shadow',
                label: {
                    formatter: (v: any) => {
                        if (v.value == 0)
                            return "Initial"
                        if (depleteTime != undefined) {
                            if (depleteTime >= 24) {
                                return "Year " + v.value;
                            } else {
                                return "Month " + v.value;
                            }
                        } else {
                            if (initialValue.withdrawalPeriod < 2) {
                                return "Month " + v.value;
                            } else {
                                return "Year " + v.value;
                            }
                        }


                    }
                }
            },

            axisLabel: {
                formatter: `${depleteTime != undefined ? depleteTime >= 24 ? 'Year {value}' : 'Month {value}'
                    : initialValue.withdrawalPeriod < 2 ? 'Month {value}' : 'Year {value}'}`
            }
        },

        yAxis: {
            type: 'value',
            axisLabel: {
                width: "10px",
                position: "right",
                formatter: (v: number) => {
                    return v.currency(1, true);
                }
            }
        },

        series: [
            {
                type: 'bar',
                name: "Portfolio Balance Left",
                data: positiveMaturityAmount?.map((e) => e?.maturityAmountPerMonth),
                tooltip: {
                    valueFormatter: function (value: number) {
                        return value.currency(0);
                    }
                },
                itemStyle: {
                    color: "#268EFF"
                },
            }
        ]
    };

    return (
        <div style={{ padding: "8px" }} ref={targetElementRef}>
            <APRow mainAxisAlignment="spaceBetween">
                <APText variant="h2" style={{ color: APPalette["grey-700"] }}>SWP Calculator</APText>
                <div ref={in4Ref} style={{ height: "16px", display: "none" }}>
                    <APImage src='/assetplus/aplight.svg' height="16px" />
                </div>
                <div ref={in3Ref}>
                    <APButton
                        type="secondary"
                        size="medium"
                        style={{ opacity: 1 }}
                        onClick={captureScreenshot}
                    >

                        <APImage src="/icons/icon-camera.svg" />

                        <APText variant="button-small" style={{ color: APPalette["brand-300"], opacity: 1 }}>Take Snapshot</APText>
                    </APButton>
                </div>
            </APRow>
            <APSizedBox height="16px" />
            <div style={{ display: "flex", flexDirection: deviceType == "mobile" ? "column-reverse" : "row" }}>

                <APExpanded flex={2}>
                    <div style={{ paddingTop: "16px", display: "flex", flexDirection: "column" }} ref={in1Ref}>

                        <CalculatorInput
                            label="Total Investment"
                            dataType="amount"
                            value={initialValue.totalInvestment}
                            sliderMax={20000000}
                            sliderMin={10000}
                            sliderStep={10000}
                            inputMin={100}
                            inputMax={1000000000}
                            onChange={(value) => {
                                setInitialValue({
                                    ...initialValue,
                                    totalInvestment: value,
                                })
                            }}
                        />

                        <APSizedBox height='24px' />

                        <CalculatorInput
                            label="Withdrawal per month"
                            dataType="amount"
                            value={initialValue.withdrawalPerMonth}
                            sliderMax={1000000}
                            sliderMin={1000}
                            sliderStep={1000}
                            inputMin={100}
                            inputMax={1000000000}
                            onChange={(value) => {
                                setInitialValue({ ...initialValue, withdrawalPerMonth: value })
                            }}
                        />
                        <APSizedBox height='24px' />
                        <CalculatorInput
                            label="Expected rate of returns"
                            dataType="percentage"
                            value={initialValue.expectedReturns}
                            sliderMax={30}
                            sliderMin={1}
                            sliderStep={1}
                            inputMin={1}
                            inputMax={30}
                            onChange={(value) => {
                                setInitialValue({ ...initialValue, expectedReturns: value })
                            }}
                        />

                        <APSizedBox height='24px' />

                        <CalculatorInput
                            label="How long you want to withdraw"
                            dataType="age"
                            value={initialValue.withdrawalPeriod}
                            sliderMax={50}
                            sliderMin={1}
                            sliderStep={1}
                            inputMin={1}
                            inputMax={50}
                            onChange={(value) => {
                                setInitialValue({ ...initialValue, withdrawalPeriod: value })
                            }}
                        />
                        <APSizedBox height="24px" />
                        <CalculatorInput
                            label="waiting period before withdrawal"
                            dataType='age'
                            value={initialValue.waitingPeriodBeforeWithdrawal}
                            sliderMax={30}
                            inputMin={0}
                            inputMax={50}
                            sliderMin={0}
                            sliderStep={1}
                            onChange={(value: any) => {
                                setInitialValue({ ...initialValue, waitingPeriodBeforeWithdrawal: value })
                            }}
                        />
                        <APSizedBox height="24px" />
                        <CalculatorInput
                            label="Increase rate of withdrawal amount"
                            dataType="percentage"
                            value={initialValue.withdrawalIncreaseRate}
                            sliderMax={30}
                            sliderMin={0}
                            sliderStep={1}
                            inputMin={0}
                            inputMax={30}
                            onChange={(value) => {
                                setInitialValue({ ...initialValue, withdrawalIncreaseRate: value })
                            }}
                        />
                    </div>
                </APExpanded>

                <APExpanded flex={2}>
                    <div style={{ paddingTop: "16px", display: "none", flexDirection: "column" }} ref={in2Ref}>
                        <ScreenShot dataType="amount" label="Principal Amount" value={initialValue.totalInvestment.currency(2, true)} />
                        <APSizedBox height="24px" />
                        <ScreenShot dataType="amount" label="Withdrawal per month" value={(initialValue.withdrawalPerMonth).currency(2, true)} />
                        <APSizedBox height='24px' />
                        <ScreenShot dataType="percentage" label="Expected rate of return" value={initialValue.expectedReturns} />
                        <APSizedBox height="24px" />
                        <ScreenShot dataType="age" label="Duration" value={initialValue.withdrawalPeriod} />
                        {(initialValue.waitingPeriodBeforeWithdrawal != 0) && (
                            <>
                                <APSizedBox height="24px" />
                                <ScreenShot dataType="age" label="Waiting Period before withdrawal" value={initialValue.waitingPeriodBeforeWithdrawal} />
                            </>
                        )}
                        {(initialValue.withdrawalIncreaseRate != 0) && (
                            <>
                                <APSizedBox height="24px" />
                                <ScreenShot dataType="percentage" label="Increase Rate of withdrawal amount" value={initialValue.withdrawalIncreaseRate} />
                            </>
                        )}
                    </div>
                </APExpanded>

                <APSizedBox width="32px" />

                <APExpanded flex={3}>
                    <APColumn crossAxisAlignment="stretch" style={{ padding: "16px", background: APPalette["grey-50"], borderRadius: "16px" }}>
                        <APCharts height="320px" option={option} />
                        <APRow mainAxisAlignment="spaceBetween"  >
                            < ChartValue style={{ color: APPalette['grey-700'] }} label={"Total Invested"} value={defaultState ? 0 : initialValue.totalInvestment} />


                            <APSizedBox >
                                <APColumn crossAxisAlignment="center" gap="6px" >
                                    <APText color={APPalette['grey-600']} variant='paragraph-small'>{"Total Withdrawal"}</APText>
                                    <APColumn crossAxisAlignment="center">
                                        <APText center={true} variant='h2'  >
                                            {
                                                isIncreasing
                                                    ? (() => {
                                                        let totalWithdrawal = 0;
                                                        var yearlyIncreaseAmount = 0;
                                                        const months = (progressiveTime ?? 0);

                                                        for (let i = 0; i < Math.floor(months / 12); i++) {
                                                            totalWithdrawal += (initialValue.withdrawalPerMonth + (yearlyIncreaseAmount)) * 12;
                                                            yearlyIncreaseAmount = yearlyIncreaseAmount + ((initialValue.withdrawalPerMonth + yearlyIncreaseAmount) * (initialValue.withdrawalIncreaseRate / 100));

                                                        }

                                                        const remainingMonths = months % 12;
                                                        totalWithdrawal += (initialValue.withdrawalPerMonth + (yearlyIncreaseAmount)) * remainingMonths;
                                                        return totalWithdrawal.currency(2, true);
                                                    })()
                                                    : corpusToggle
                                                        ? `~ ${(() => {
                                                            let totalWithdrawal = 0;
                                                            var yearlyIncreaseAmount = 0
                                                            const months = depleteTime;

                                                            for (let i = 0; i < Math.floor(months / 12); i++) {
                                                                totalWithdrawal += (initialValue.withdrawalPerMonth + (yearlyIncreaseAmount)) * 12;
                                                                yearlyIncreaseAmount = yearlyIncreaseAmount + ((initialValue.withdrawalPerMonth + yearlyIncreaseAmount) * (initialValue.withdrawalIncreaseRate / 100));

                                                            }

                                                            const remainingMonths = months % 12;
                                                            totalWithdrawal += (initialValue.withdrawalPerMonth + yearlyIncreaseAmount) * remainingMonths;
                                                            return totalWithdrawal;
                                                        })().currency(2, true)
                                                        }`
                                                        : `${(() => {
                                                            let totalWithdrawal = 0;
                                                            var yearlyIncreaseAmount = 0;
                                                            const months = depleteTime;

                                                            for (let i = 0; i < Math.floor(months / 12); i++) {
                                                                totalWithdrawal += (initialValue.withdrawalPerMonth + (yearlyIncreaseAmount)) * 12;
                                                                yearlyIncreaseAmount = yearlyIncreaseAmount + ((initialValue.withdrawalPerMonth + yearlyIncreaseAmount) * (initialValue.withdrawalIncreaseRate / 100));

                                                            }

                                                            const remainingMonths = months % 12;
                                                            totalWithdrawal += (initialValue.withdrawalPerMonth + yearlyIncreaseAmount) * remainingMonths;
                                                            return totalWithdrawal;
                                                        })().currency(2, true)}`
                                            }
                                        </APText>
                                    </APColumn>
                                </APColumn>
                            </APSizedBox>

                            < ChartValue label={"Final Portfolio Balance"} value={corpusToggle ? 0 : maturityAmount ?? 0} />

                        </APRow>
                        <APSizedBox height='16px' />
                        {
                            defaultState == false && corpusToggle
                                ?
                                <APText variant='paragraph-large' style={{ color: APPalette['grey-700'] }}>
                                    Your Corpus will deplete in approximately
                                    <span style={{ fontWeight: 600 }}>{depleteTime <= 12 ? <> {depleteTime}-{depleteTime + 1} months </> : getYearMonth(depleteTime)}</span>
                                    if you withdraw
                                    <span style={{ fontWeight: 600 }} > {(initialValue.withdrawalPerMonth).currency(0, false)} per month</span>.
                                    To last <span style={{ fontWeight: 600 }}>{initialValue.withdrawalPeriod} years</span>, you should consider withdrawing approximately
                                    <span style={{ fontWeight: 600 }}> {(shouldWithdrawPerMonth?.currency(2, false))} per month</span>.
                                </APText>
                                : undefined

                        }
                    </APColumn>
                </APExpanded>

            </div>

            <APSizedBox height="8px" />
            <APText variant="paragraph-xsmall">Calculators are for illustrations only and do not represent actual returns. <br /> Mutual Fund investments are subject to market risks; read all scheme-related documents carefully.</APText>
            <div ref={in5Ref} style={{ display: "none" }}>
                <APRow>
                    <APText variant="paragraph-small" style={{ color: APPalette["grey-500"] }}>Powered by&nbsp;</APText>
                    <APImage src='/assetplus/aplight.svg' height="10px" style={{ marginTop: "2px" }} />
                </APRow>
            </div>
        </div >
    )
}
